import { Button, Text, TextInput, Section, Box, Loader } from '@acast/decibel';
import { SearchRegularGlyph } from '@acast/decibel-glyphs';
import Preferences from 'app/preferences';
import { selectPreferenceValue, updatePreference } from 'ducks/user';
import get from 'lodash.get';
import T from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import navigateTo from 'shared/helpers/navigate-to';
import OverviewShowsListView from './overview-shows-list-view';

const DEFAULT_SORT_OPTIONS = {
  sortKey: 'name',
  sortAscending: true,
};

const createComparatorForKeyAndAscending = (key, isAscending) => {
  return (first, second) => {
    const firstValue = get(first, key);
    const secondValue = get(second, key);
    if (firstValue > secondValue) {
      return isAscending ? 1 : -1;
    }
    if (firstValue < secondValue) {
      return isAscending ? -1 : 1;
    }
    return 0;
  };
};

const sortOnKey = (collection, key, isAscending) => {
  const sortKey = key === DEFAULT_SORT_OPTIONS.sortKey ? key : DEFAULT_SORT_OPTIONS.sortKey;
  return collection.sort(createComparatorForKeyAndAscending(sortKey, isAscending));
};

const filterByName = (collection, filter) => {
  if (!filter) {
    return collection;
  }

  const filterWords = filter.split(/\s+/).map(word => word.toLowerCase());

  return collection.filter(show =>
    filterWords.every(filterWord => show.name.toLowerCase().includes(filterWord))
  );
};

const handleClickNewShow = () => navigateTo('/shows/new');

class OverviewShows extends Component {
  constructor(props) {
    super(props);

    const { sortOptions } = this.props;
    this.state = {
      ...sortOptions,
      filter: '',
    };
  }

  filterAndSortShows = () => {
    const { shows } = this.props;
    const showArray = Object.values(shows.items);
    const filtered = filterByName(showArray, this.state.filter);
    return sortOnKey(filtered, this.state.sortKey, this.state.sortAscending);
  };

  maybeRenderNewShowLink = () => {
    if (!this.props.isAllowedToCreateNewShow) {
      return null;
    }
    return (
      <Button
        variant="primary"
        className="overview-shows-new-show"
        size="small"
        onClick={handleClickNewShow}
        data-test-id="OverviewShows__newShowButton"
      >
        New Show
      </Button>
    );
  };

  sortOnKeyAscending = sortKey => {
    const sortAscending = !this.state.sortAscending;
    this.setState({ sortKey, sortAscending });

    this.props.updatePreference({
      name: Preferences.OVERVIEW_SHOWS_SORTING,
      value: {
        sortKey,
        sortAscending,
      },
    });
  };

  renderListView = () => {
    return (
      <OverviewShowsListView
        shows={this.filterAndSortShows()}
        sortOnKeyAscending={this.sortOnKeyAscending}
        sortKey={this.state.sortKey}
        sortAscending={this.state.sortAscending}
      />
    );
  };

  renderShows = () => {
    if (!Object.keys(this.props.shows.items).length) {
      return (
        <Box minHeight={85} alignItems="center" justifyContent="center">
          <Loader />
        </Box>
      );
    }

    return this.renderListView();
  };

  renderFilter = () => {
    return (
      <TextInput
        glyph={SearchRegularGlyph}
        value={this.state.filter}
        onChange={event => this.setState({ filter: event.target.value })}
        placeholder="Filter Shows"
        size="small"
        data-test-id="OverviewShows__filter"
      />
    );
  };

  render() {
    const { shows } = this.props;
    return (
      <Section>
        <Box justifyContent="space-between" mb={4}>
          <Text variant="h3" mb={0}>
            All Shows{shows.total != null && ` (${shows.total})`}
          </Text>
          <Box gap={4}>
            {this.renderFilter()}
            {this.maybeRenderNewShowLink()}
          </Box>
        </Box>
        {this.renderShows()}
      </Section>
    );
  }
}

OverviewShows.propTypes = {
  shows: T.object,
  isAllowedToCreateNewShow: T.bool,
  sortOptions: T.object,
  updatePreference: T.func.isRequired,
};

export default connect(
  state => ({
    sortOptions: selectPreferenceValue(Preferences.OVERVIEW_SHOWS_SORTING, DEFAULT_SORT_OPTIONS)(
      state
    ),
  }),
  { updatePreference }
)(OverviewShows);
